import type { LucideIcon } from 'lucide-react';
import type { MemberRole } from '@repo/shared-types';
import { settingsSectionsForRole, type SettingsSection } from '../../pages/settings/settingsNav';

/** An org this user could switch into. Nothing populates this before THI-73 —
 *  `authStore` has no memberships array today — but the shape is fixed here so
 *  the switcher slot is a typed extension point rather than a rewrite. */
export type OtherOrg = { id: string; name: string; logoUrl?: string | null };

export type OrgMenuLink = {
  key: SettingsSection;
  href: string;
  icon: LucideIcon;
  label: { ns: 'settings'; key: string };
  /** Optional 11px muted second line under the label. */
  hint?: { ns: 'common'; key: string };
};

export type OrgMenuGroup =
  | { kind: 'links'; key: 'org' | 'account'; label: { ns: 'settings' | 'common'; key: string }; items: OrgMenuLink[] }
  | { kind: 'switcher'; key: 'switcher'; label: { ns: 'common'; key: string }; orgs: OtherOrg[] };

const HINTS: Partial<Record<SettingsSection, { ns: 'common'; key: string }>> = {
  members: { ns: 'common', key: 'orgMenu.hint.members' },
};

/**
 * The org menu's groups = the settings nav's groups (`SETTINGS_GROUPS`, already
 * role-filtered by `settingsSectionsForRole`) turned into `/settings/:section`
 * links, with the future org-switcher slot spliced in above the account group.
 * Deriving rather than restating is the point: a section added in
 * `settingsNav.ts` shows up here, and the two can never disagree.
 *
 * The account group's label comes from `common` instead of `settings` because
 * it interpolates the user's name ("Tài khoản — {{name}}").
 */
export function orgMenuGroups(role: MemberRole | null, otherOrgs: OtherOrg[] = []): OrgMenuGroup[] {
  const groups: OrgMenuGroup[] = [];
  for (const g of settingsSectionsForRole(role)) {
    // Never a one-entry dropdown: the slot exists only when this user actually
    // has somewhere else to go.
    if (g.key === 'account' && otherOrgs.length > 0) {
      groups.push({ kind: 'switcher', key: 'switcher', label: { ns: 'common', key: 'orgMenu.switchOrg' }, orgs: otherOrgs });
    }
    groups.push({
      kind: 'links',
      key: g.key,
      label: g.key === 'account' ? { ns: 'common', key: 'orgMenu.accountGroup' } : g.label,
      items: g.items.map((i) => ({ key: i.key, href: `/settings/${i.key}`, icon: i.icon, label: i.label, hint: HINTS[i.key] })),
    });
  }
  return groups;
}

/** Menu-header plan word: the plan name upper-cased, the trial label while
 *  TRIALING, "Free" when the org has no subscription at all. Deliberately not
 *  shared with the sidebar *header* badge, which hides itself when there is no
 *  subscription and must keep doing so. */
export function planLabel(
  subscription: { status: string; plan: { name: string } } | undefined,
  labels: { trial: string; free: string },
): string {
  if (!subscription) return labels.free;
  if (subscription.status === 'TRIALING') return labels.trial;
  return subscription.plan.name.toUpperCase();
}

/** Two-letter fallback for an org (or person) with no logo. */
export function orgInitials(name: string | null | undefined): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '??';
  return words.map((w) => w[0]).slice(0, 2).join('').toUpperCase();
}
